<template>
  <template v-if="canManage">
    <div
      v-if="loadingDocuments || loadingEffectiveBeneficiaries || loadingAccountCreator"
      class="flex justify-center py-8"
    >
      <v-progress-circular indeterminate color="primary" width="2" size="24" />
    </div>
    <div v-else class="flex flex-col gap-6">
      <OpeningCard :title="t('treezorOnboarding.documents.title')">
        <div class="flex flex-col gap-10">
          <div
            v-for="requiredDoc in companyDocuments"
            :key="requiredDoc.documentTypeId"
            class="flex flex-col gap-3"
          >
            <div class="text-base font-semibold">
              {{ t(`treezorOnboarding.documents.${requiredDoc.labelKey}`) }}
              <span class="text-error text-lg leading-none">*</span>
            </div>

            <KycDocumentItem
              v-for="document in requiredDoc.documents"
              :key="document.documentId"
              :document="document"
              :label-key="requiredDoc.labelKey"
            />

            <div class="mt-2 flex flex-col gap-1">
              <v-file-input
                :model-value="documentState[requiredDoc.documentTypeId].file"
                class="min-w-60 grow"
                density="compact"
                variant="outlined"
                prepend-inner-icon="mdi-paperclip"
                :prepend-icon="''"
                hide-details="auto"
                accept=".jpg,.jpeg,.png,.svg,.tif,.tiff,.pdf,image/jpeg,image/png,image/svg+xml,image/tiff,application/pdf"
                :rules="[maxFileSize(50)]"
                :error-messages="
                  showMissing &&
                  kycReviewReadiness.missingDocumentTypeIds.includes(requiredDoc.documentTypeId)
                    ? t('errors.required')
                    : []
                "
                :loading="documentState[requiredDoc.documentTypeId].uploading"
                :disabled="documentState[requiredDoc.documentTypeId].uploading || !canEditKyc"
                :label="t('treezorOnboarding.documents.chooseFile')"
                @update:model-value="onDocumentFileUpdate(requiredDoc.documentTypeId, $event)"
              />
              <p class="text-xs text-medium-emphasis m-0">
                {{ t("treezorOnboarding.documents.hint") }}
              </p>
            </div>
          </div>
        </div>
      </OpeningCard>

      <OpeningCard v-for="person in people" :key="person.userId" :title="person.name">
        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-10">
            <KycPersonDocumentUpload
              v-for="requirement in person.requirements"
              :key="requirement.id"
              :user-id="person.userId"
              :requirement="requirement"
              :documents="person.documents"
              :show-missing="showMissing"
            />
          </div>
        </div>
      </OpeningCard>
    </div>

    <div class="mt-6 flex flex-wrap items-center justify-end gap-2">
      <v-btn
        class="me-auto"
        variant="text"
        prepend-icon="mdi-chevron-left"
        :disabled="busy || markingReady || submittingForReview || refusing"
        @click="emit('previous')"
      >
        {{ t("common.previous") }}
      </v-btn>
      <v-btn
        v-if="user.isKind('admin') && kycReadyForAdminReview && !submittedForReview"
        color="error"
        variant="flat"
        :disabled="submittingForReview || markingReady || refusing"
        @click="showRefuseDialog = true"
      >
        {{ t("treezorOnboarding.review.refuse") }}
      </v-btn>
      <v-btn
        v-if="user.isKind('admin')"
        color="primary"
        variant="flat"
        :disabled="
          !kycSubmitReadiness.dataComplete ||
          submittedForReview ||
          submittingForReview ||
          markingReady ||
          refusing
        "
        :loading="submittingForReview"
        @click="onSubmitForReview"
      >
        {{ t("treezorOnboarding.review.submit") }}
      </v-btn>
      <v-btn
        size="large"
        color="primary"
        variant="flat"
        :disabled="
          busy ||
          kycReadyForAdminReview ||
          submittedForReview ||
          markingReady ||
          submittingForReview ||
          refusing
        "
        :loading="markingReady"
        @click="onOpenAccount"
      >
        {{ t("treezorOnboarding.review.requestIban") }}
      </v-btn>
    </div>

    <v-dialog v-model="showRefuseDialog" width="500" persistent>
      <v-card>
        <v-card-title>{{ t("treezorOnboarding.review.refuseTitle") }}</v-card-title>
        <v-card-text>
          <p class="mb-4">{{ t("treezorOnboarding.review.refuseMessage") }}</p>
          <v-form :disabled="refusing" @submit.prevent>
            <v-textarea
              v-model.trim="refuseReason"
              :label="t('treezorOnboarding.review.refuseReason')"
              auto-grow
              rows="3"
              autofocus
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn :disabled="refusing" @click="showRefuseDialog = false">
            {{ t("common.cancel") }}
          </v-btn>
          <v-btn
            color="error"
            variant="flat"
            :loading="refusing"
            :disabled="refusing || !refuseReason"
            @click="onRefuse"
          >
            {{ t("treezorOnboarding.review.refuse") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </template>
</template>

<script lang="ts" setup>
import { useI18n } from "@/composables/useI18n";
import { useRules } from "@/composables/useRules";
import { call } from "@/firebase";
import { useToast, useUserStore } from "@/store";
import { RelationshipTypeId } from "@aircap/common/treezor";
import { captureException } from "@sentry/vue";
import { computed, reactive, ref, watch } from "vue";
import { useRouter } from "vue-router";
import KycDocumentItem from "../KycDocumentItem.vue";
import KycPersonDocumentUpload from "../KycPersonDocumentUpload.vue";
import OpeningCard from "../OpeningCard.vue";
import {
  effectiveBeneficiaryDocumentRequirementsForForm,
  requiredDocuments,
  useTreezorOnboarding,
  type FileInputValue,
  type RequiredDocumentTypeId,
} from "../useTreezorOnboarding";

defineProps<{ showMissing: boolean }>();
const emit = defineEmits<{ previous: []; "open-account": [] }>();

const { t } = useI18n();
const { maxFileSize } = useRules();
const { showSuccessToast, showErrorToast } = useToast();
const user = useUserStore();
const router = useRouter();
const {
  accountId,
  canManage,
  canEditKyc,
  treezorDocuments,
  loadingDocuments,
  loadDocuments,
  uploadTreezorDocumentFile,
  effectiveBeneficiaries,
  loadingEffectiveBeneficiaries,
  effectiveBeneficiaryForms,
  effectiveBeneficiaryDocumentState,
  accountCreator,
  loadingAccountCreator,
  accountCreatorDocumentRequirements,
  effectiveBeneficiariesBusy,
  kycReviewReadiness,
  kycSubmitReadiness,
  submittedForReview,
  kycReadyForAdminReview,
} = useTreezorOnboarding();

const markingReady = ref(false);
const submittingForReview = ref(false);
const showRefuseDialog = ref(false);
const refuseReason = ref("");
watch(accountId, () => {
  showRefuseDialog.value = false;
  refuseReason.value = "";
});
const refusing = ref(false);

const documentState = reactive(
  Object.fromEntries(
    requiredDocuments.map((requiredDoc) => [
      requiredDoc.documentTypeId,
      { file: null as File | null, uploading: false },
    ]),
  ) as Record<RequiredDocumentTypeId, { file: File | null; uploading: boolean }>,
);

// Person uploads are covered by `effectiveBeneficiariesBusy`.
const busy = computed(
  () =>
    effectiveBeneficiariesBusy.value ||
    Object.values(documentState).some((state) => state.uploading),
);

const onDocumentFileUpdate = (documentTypeId: RequiredDocumentTypeId, value: FileInputValue) => {
  if (documentState[documentTypeId].uploading) return;
  const file = Array.isArray(value) ? (value[0] ?? null) : value;
  documentState[documentTypeId].file = file;
  if (!file || maxFileSize(50)(file) !== true) return;
  void onUploadDocument(documentTypeId);
};

const onUploadDocument = async (documentTypeId: RequiredDocumentTypeId) => {
  if (!canEditKyc.value) return;
  const file = documentState[documentTypeId].file;
  if (!file || maxFileSize(50)(file) !== true) return;

  documentState[documentTypeId].uploading = true;
  try {
    const documentId = await uploadTreezorDocumentFile({ file, documentTypeId });
    if (!documentId) return;
    let found = false;
    for (let attempt = 0; attempt < 8; attempt++) {
      await loadDocuments({ silent: true });
      if (treezorDocuments.value.some((document) => document.documentId === documentId)) {
        found = true;
        break;
      }
      await new Promise((resolve) => {
        setTimeout(resolve, 1000);
      });
    }
    if (!found) throw new Error("Uploaded document was not found after upload");
    showSuccessToast(t("treezorOnboarding.documents.uploadSuccess"));
  } catch (cause) {
    const error = new Error("Error uploading Treezor document", { cause });
    console.error(error);
    captureException(error);
    showErrorToast(cause instanceof Error ? cause.message : undefined);
  } finally {
    documentState[documentTypeId].file = null;
    documentState[documentTypeId].uploading = false;
  }
};

const companyDocuments = computed(() =>
  requiredDocuments.map((requiredDoc) => ({
    ...requiredDoc,
    documents: treezorDocuments.value.filter(
      (document) => document.documentType === requiredDoc.documentTypeId,
    ),
  })),
);

// Mirrors the requirements `effectiveBeneficiaryDocumentState` is synced from. People who
// do not exist in Treezor yet are not listed: they cannot upload.
const people = computed(() =>
  effectiveBeneficiaries.value.flatMap((effectiveBeneficiary) => {
    const form = effectiveBeneficiaryForms.value[effectiveBeneficiary.user.userId];
    if (!form) return [];
    const uploads = effectiveBeneficiaryDocumentState.value[effectiveBeneficiary.user.userId];
    const isAccountCreator = effectiveBeneficiary.user.userId === accountCreator.value?.userId;
    const requirements = [
      ...effectiveBeneficiaryDocumentRequirementsForForm(form, isAccountCreator),
      ...(isAccountCreator ? accountCreatorDocumentRequirements.value : []),
    ].filter((requirement) => uploads?.[requirement.id]);
    if (requirements.length === 0) return [];
    const name =
      [form.firstname, form.lastname].filter(Boolean).join(" ") ||
      t(
        form.relationshipTypeIds.includes(RelationshipTypeId.LegalRepresentative)
          ? "treezorOnboarding.effectiveBeneficiaries.types.LEGAL_REPRESENTATIVE"
          : "treezorOnboarding.effectiveBeneficiaries.unnamed",
      );
    return [
      {
        userId: effectiveBeneficiary.user.userId,
        name,
        isAccountCreator,
        requirements,
        documents: effectiveBeneficiary.documents,
      },
    ];
  }),
);

// The parent flags every step as missing first, so a click on an incomplete file shows what is left.
const onOpenAccount = async () => {
  emit("open-account");
  if (!kycReviewReadiness.value.dataComplete) {
    showErrorToast(t("treezorOnboarding.steps.missingInformation"));
    return;
  }
  if (kycReadyForAdminReview.value || submittedForReview.value) return;

  const requestedAccountId = accountId.value;
  markingReady.value = true;
  try {
    await call("markTreezorKycReadyForAdminReview")({ accountId: requestedAccountId });
    if (accountId.value === requestedAccountId)
      await router.push({ name: "Dashboard", params: { accountId: requestedAccountId } });
  } catch (cause) {
    const error = new Error("Error marking Treezor KYC ready for admin review", { cause });
    console.error(error);
    captureException(error);
    showErrorToast(cause instanceof Error ? cause.message : undefined);
  } finally {
    markingReady.value = false;
  }
};

const onSubmitForReview = async () => {
  if (!user.isKind("admin") || !kycSubmitReadiness.value.dataComplete || submittedForReview.value)
    return;

  const requestedAccountId = accountId.value;
  submittingForReview.value = true;
  try {
    await call("reviewTreezorKycAdmin")({ accountId: requestedAccountId, status: "validated" });
    if (accountId.value !== requestedAccountId) return;
    showSuccessToast(t("treezorOnboarding.review.submitted"));
  } catch (cause) {
    const error = new Error("Error validating Treezor KYC admin review", { cause });
    console.error(error);
    captureException(error);
    showErrorToast(cause instanceof Error ? cause.message : undefined);
  } finally {
    submittingForReview.value = false;
  }
};

const onRefuse = async () => {
  if (!user.isKind("admin") || !refuseReason.value) return;

  const requestedAccountId = accountId.value;
  refusing.value = true;
  try {
    await call("reviewTreezorKycAdmin")({
      accountId: requestedAccountId,
      status: "refused",
      reason: refuseReason.value,
    });
    if (accountId.value !== requestedAccountId) return;
    showSuccessToast(t("treezorOnboarding.review.refused"));
    showRefuseDialog.value = false;
    refuseReason.value = "";
  } catch (cause) {
    const error = new Error("Error refusing Treezor KYC admin review", { cause });
    console.error(error);
    captureException(error);
    showErrorToast(cause instanceof Error ? cause.message : undefined);
  } finally {
    refusing.value = false;
  }
};
</script>
